import $ from 'jquery';

import AccountAddressViewHelper from 'chairisher/view/helper/accountaddress';
import AlerterView from 'chairisher/view/alerter';
import CountryView from 'chairisher/view/account/countries';

import { isAddressAutocompleteEnabled, isAddressVerificationEnabled } from 'chairisher/context/google';
import { handleAsyncError } from 'chairisher/util/logging';

/**
 * Manages a modal for displaying an account's saved addresses.
 */
class AddressModal {
    constructor() {
        this.$el = $('.js-address-modal');
        this.$addressNewModal = $('.js-address-new-modal');
        this.$addressSavedModal = $('.js-address-saved-modal');
        this.$addressSubmitBtn = $('.js-address-submit-btn');
        this.$createNewAddressForm = this.$addressNewModal.find('#js-account-address-update-form');
        this.$expandAddressesBtn = $('.js-expand-addresses-btn');
        this.$newAddressBtn = $('.js-new-address-btn');
        this.accountAddressMap = {};
        this.isCollapsed = true;
        this.MAX_COLLAPSED_ADDRESSES = 5;
        this.accountAddresses = [];
    }

    /**
     * Binds actions related to the modal
     */
    bind() {
        this.$el.on('hidden.bs.modal', () => {
            this.resetModalUi();
            AccountAddressViewHelper.resetForm(this.$createNewAddressForm);
        });

        //
        // Saved Address Modal Bindings
        //

        this.$expandAddressesBtn.on('click', () => {
            this.toggleSubmitBtnDisabled(true);
            this.setCollapsedState(false);
            this.populateAddressList();
        });

        this.$newAddressBtn.on('click', () => {
            this.$addressSavedModal.addClass('hidden');
            this.$addressNewModal.removeClass('hidden');
        });

        this.$addressSubmitBtn.on('click', () => {
            const accountAddressId = this.$addressSavedModal.find('[name="address"]:checked').val();
            this.eventTrigger('change', {
                accountAddress: this.accountAddressMap[accountAddressId],
            });
            this.$el.modal('hide');
        });

        //
        // New Address Modal Bindings
        //

        // swap fields based on country code
        const $countryEl = this.$el.find('.js-address-country');
        if ($countryEl.length > 0) {
            const countryView = new CountryView({ $countryEl });
            countryView.bind();
        }

        if (isAddressAutocompleteEnabled()) {
            import('chairisher/component/autocomplete/google/address').then(
                ({ default: GoogleAutocompleteAddress }) => {
                    const googleAutocompleteAddress = new GoogleAutocompleteAddress({
                        $addressFields: this.$createNewAddressForm,
                    });
                    googleAutocompleteAddress.bind();
                },
                handleAsyncError,
            );
        }

        AccountAddressViewHelper.bindStreetAddressLine2BtnCollapse(this.$createNewAddressForm);

        this.$createNewAddressForm.on('submit', async (e) => {
            e.preventDefault();
            if (isAddressVerificationEnabled()) {
                const { default: GoogleAddressVerification } = await import(
                    'chairisher/view/google/addressverification'
                );
                GoogleAddressVerification.triggerVerification({
                    $addressFields: this.$createNewAddressForm,
                    callback: () => this.createNewAccountAddress(),
                });
            } else {
                this.createNewAccountAddress();
            }
        });
    }

    /**
     * Creates a new address based on form data
     *
     * @returns {$.Deferred}
     */
    createNewAccountAddress() {
        const requestObject = {
            data: this.$createNewAddressForm.serializeArray(),
            method: this.$createNewAddressForm.attr('method'),
            url: this.$createNewAddressForm.attr('action'),
        };

        return $.ajax(requestObject)
            .done((response) => {
                const { data } = response;
                this.eventTrigger('change', {
                    address: data && data.length ? data[0] : {},
                });
                this.$el.modal('hide');
            })
            .fail((response) => {
                const { responseJSON } = response;
                if (responseJSON.errors) {
                    AccountAddressViewHelper.setFormErrors(this.$createNewAddressForm, responseJSON.errors);
                } else {
                    AlerterView.error();
                    this.$el.modal('hide');
                }
            });
    }

    /**
     * Triggers a new event while passing along any other additional data provided
     *
     * @param {string} type The new event's type
     * @param {Object=} data Additional data to be passed along as an argument to the event handler
     * @private
     */
    eventTrigger(type, data) {
        this.$el.trigger($.Event(`addressmodal:${type}`), data);
    }

    /**
     * @returns {jQuery} The wrapper element that the modal elements are contained in
     */
    getEl() {
        return this.$el;
    }

    /**
     * Hides the modal
     */
    hide() {
        this.toggle(false);
    }

    /**
     * Populates the address items in the DOM
     */
    populateAddressList() {
        const accountAddresses = this.isCollapsed
            ? this.accountAddresses.slice(0, this.MAX_COLLAPSED_ADDRESSES)
            : this.accountAddresses;
        this.$addressSavedModal.find('.js-address-list').html(accountAddresses.map(AddressModal.marshalAddressRow));
    }

    /**
     * Renders the modal for a new address
     */
    renderNew() {
        this.$addressNewModal.removeClass('hidden');
        this.show();
    }

    /**
     * Renders the modal for saved addresses
     *
     * @param {Array} accountAddresses An array of account address objects
     * @param {boolean} isChangeable Whether or not the modal can be changed from "saved" to "new"
     */
    renderSaved(accountAddresses, isChangeable) {
        this.setAccountAddresses(accountAddresses);
        this.setCollapsedState(this.accountAddresses.length > this.MAX_COLLAPSED_ADDRESSES);
        this.populateAddressList();
        this.$newAddressBtn.toggleClass('hidden', !isChangeable);
        this.$addressSavedModal.removeClass('hidden');
        this.show();
    }

    /**
     * Resets the modal ui to its initial default state
     */
    resetModalUi() {
        this.$addressNewModal.add(this.$addressSavedModal).addClass('hidden');
        this.toggleSubmitBtnDisabled(true);
    }

    /**
     * Prepares the modal to either display its collapsed or expanded version
     *
     * @param {boolean} isCollapsed Whether or not the modal is currently in a collapsed state
     */
    setCollapsedState(isCollapsed) {
        this.isCollapsed = isCollapsed;
        this.$expandAddressesBtn.toggleClass('hidden', !this.isCollapsed);
    }

    /**
     * Sets the list of account addresses
     *
     * @param accountAddresses {Array}
     */
    setAccountAddresses(accountAddresses) {
        accountAddresses.forEach((accountAddress) => {
            this.accountAddressMap[accountAddress.id.value] = accountAddress;
        });
        this.accountAddresses = accountAddresses;
    }

    /**
     * Displays the modal
     */
    show() {
        this.toggle(true);
    }

    /**
     * Toggles the modal
     *
     * @param {boolean|undefined} shouldShow True shows the modal, false hides the modal, undefined toggles the modal
     */
    toggle(shouldShow) {
        let action = 'toggle';
        if (shouldShow !== undefined) {
            action = shouldShow ? 'show' : 'hide';
        }
        this.$el.modal(action);
    }

    /**
     * Toggles whether the submit button is disabled.
     *
     * @param shouldDisable {boolean}
     */
    toggleSubmitBtnDisabled(shouldDisable) {
        this.$el.find('.js-address-submit-btn').prop('disabled', shouldDisable);
    }

    /**
     * Given an address object, creates a jQuery element with formatted address data.
     *
     * @param {Object} accountAddress
     * @returns {jQuery}
     */
    static marshalAddressRow(accountAddress) {
        const $addressRow = $('<li></li>').append($($('#js-template-boolean-input').html()));

        const $booleanInput = $addressRow.find('input');
        $booleanInput.attr({
            name: 'address',
            value: accountAddress.id.value,
        });

        $booleanInput.on('click', () => {
            $('.js-address-submit-btn').prop('disabled', false);
        });

        let addressString = [
            accountAddress.name.value,
            accountAddress.address.street_address_line_1.value,
            accountAddress.address.street_address_line_2.value,
            accountAddress.address.city.value,
            accountAddress.address.state.value,
        ]
            .filter((a) => !!a)
            .join(', ');

        addressString += ` ${accountAddress.address.postal_code.value} ${accountAddress.address.country_code.display}`;

        $addressRow.find('.js-choice-text').text(addressString);

        return $addressRow;
    }
}

export default AddressModal;
